import { useState } from "react";

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=Noto+Serif+Devanagari:wght@300;400;500;600;700&family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&display=swap');

  :root {
    --ink: #1a1008;
    --ink-mid: #2d1f10;
    --ink-light: #4a3728;
    --paper: #f5f0e3;
    --paper-aged: #ede5cc;
    --paper-dark: #e4d9c0;
    --red: #8b1a1a;
    --gold: #9a7b2e;
    --rule: #8b7355;
    --border: #3d2b1f;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }

  body { background: #d4c9b0; min-height: 100vh; }

  .epaper-wrapper {
    background: #cdc0a5;
    min-height: 100vh;
    padding: 24px 16px;
    font-family: 'Noto Serif Devanagari', 'Libre Baskerville', Georgia, serif;
  }

  .newspaper {
    max-width: 1140px;
    margin: 0 auto;
    background: var(--paper);
    border: 2px solid var(--border);
    box-shadow: 6px 6px 0 #b8a882, 12px 12px 0 #a09070, 0 20px 60px rgba(0,0,0,0.35);
    position: relative;
    animation: fadeIn 0.6s ease;
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(12px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .newspaper::before {
    content: '';
    position: absolute;
    inset: 5px;
    border: 1px solid var(--rule);
    pointer-events: none;
    z-index: 5;
  }

  /* TOP STRIP */
  .top-strip {
    background: var(--ink);
    color: var(--paper);
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 4px 20px;
    font-size: 10px;
    letter-spacing: 0.4px;
  }

  /* MASTHEAD */
  .masthead {
    padding: 12px 20px 10px;
    border-bottom: 3px double var(--border);
    background: var(--paper);
  }

  .masthead-inner {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  .masthead-left, .masthead-right {
    font-size: 10px;
    line-height: 1.8;
    color: var(--ink-mid);
    min-width: 140px;
  }

  .masthead-right { text-align: right; }

  .masthead-center { text-align: center; flex: 1; padding: 0 16px; }

  .paper-title {
    font-family: 'Playfair Display', serif;
    font-size: 58px;
    font-weight: 900;
    color: var(--ink);
    line-height: 1;
    letter-spacing: -2px;
    text-shadow: 2px 2px 0 rgba(0,0,0,0.1);
  }

  .paper-hindi {
    font-family: 'Noto Serif Devanagari', serif;
    font-size: 13px;
    font-weight: 600;
    color: var(--red);
    letter-spacing: 2.5px;
    margin-top: 3px;
    display: block;
  }

  .paper-tagline {
    font-size: 10px;
    color: var(--rule);
    font-style: italic;
    margin-top: 2px;
    letter-spacing: 0.5px;
  }

  /* EDITION BAR */
  .edition-bar {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 5px 20px;
    background: var(--paper-aged);
    border-bottom: 2px solid var(--border);
    font-size: 10.5px;
    font-weight: 600;
  }

  .price-tag {
    background: var(--red);
    color: white;
    padding: 2px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.5px;
  }

  /* BREAKING BAR */
  .breaking-bar {
    background: var(--red);
    color: white;
    padding: 5px 20px;
    font-size: 10.5px;
    font-weight: 700;
    overflow: hidden;
    white-space: nowrap;
    border-bottom: 2px solid var(--border);
  }

  .ticker-content {
    display: inline-block;
    animation: ticker 28s linear infinite;
  }

  @keyframes ticker {
    0% { transform: translateX(100%); }
    100% { transform: translateX(-100%); }
  }

  /* PAGE SELECTOR */
  .page-tabs {
    display: flex;
    background: var(--paper-dark);
    border-bottom: 2px solid var(--border);
    padding: 0 16px;
    gap: 2px;
  }

  .page-tab {
    padding: 7px 18px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;
    border: none;
    background: transparent;
    color: var(--ink-light);
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    font-family: 'Noto Serif Devanagari', serif;
    transition: all 0.2s;
  }

  .page-tab:hover { color: var(--red); }
  .page-tab.active {
    color: var(--red);
    border-bottom-color: var(--red);
    background: var(--paper);
  }

  /* CONTENT */
  .content { padding: 14px 18px; }

  /* GRID */
  .grid-6 { display: grid; grid-template-columns: repeat(6, 1fr); }
  .grid-5 { display: grid; grid-template-columns: repeat(5, 1fr); }
  .grid-4 { display: grid; grid-template-columns: repeat(4, 1fr); }
  .grid-3 { display: grid; grid-template-columns: repeat(3, 1fr); }
  .grid-2 { display: grid; grid-template-columns: repeat(2, 1fr); }

  .col-2 { grid-column: span 2; }
  .col-3 { grid-column: span 3; }
  .col-4 { grid-column: span 4; }

  /* ARTICLE */
  .article {
    padding: 0 11px;
    position: relative;
    transition: background 0.2s;
  }

  .article:hover { background: rgba(139,122,86,0.04); }

  .article + .article { border-left: 1px solid var(--rule); }
  .article:first-child { border-left: none; padding-left: 4px; }

  .section-label {
    font-size: 8.5px;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: 700;
    color: var(--red);
    border-bottom: 1px solid var(--red);
    padding-bottom: 2px;
    margin-bottom: 5px;
    display: inline-block;
  }

  .hl { 
    font-family: 'Noto Serif Devanagari', serif;
    font-weight: 700;
    line-height: 1.3;
    color: var(--ink);
    margin-bottom: 5px;
  }

  .hl-xl { font-size: 20px; }
  .hl-lg { font-size: 16px; }
  .hl-md { font-size: 13.5px; }
  .hl-sm { font-size: 12px; }
  .hl-red { color: var(--red); }

  .byline {
    font-size: 8.5px;
    color: var(--gold);
    font-style: italic;
    margin-bottom: 5px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .body {
    font-size: 10.5px;
    line-height: 1.68;
    color: var(--ink);
    text-align: justify;
  }

  .body p { margin-bottom: 5px; }

  .drop-cap::first-letter {
    font-family: 'Playfair Display', serif;
    font-size: 42px;
    font-weight: 900;
    float: left;
    line-height: 0.82;
    margin-right: 5px;
    margin-top: 4px;
    color: var(--red);
  }

  /* DIVIDERS */
  .rule { border: none; border-top: 2px solid var(--border); margin: 10px 0; }
  .rule-thin { border: none; border-top: 1px solid var(--rule); margin: 8px 0; }
  .rule-dbl { border: none; border-top: 3px double var(--border); margin: 10px 0; }

  /* PHOTO BLOCK */
  .photo {
    background: linear-gradient(145deg, #d8ceb8 0%, #c8bda0 100%);
    border: 1px solid var(--rule);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: 4px;
    color: var(--ink-light);
    font-size: 9px;
    font-style: italic;
    margin-bottom: 7px;
    overflow: hidden;
    position: relative;
  }

  .photo::after {
    content: '';
    position: absolute;
    inset: 0;
    background: repeating-linear-gradient(
      45deg,
      transparent,
      transparent 2px,
      rgba(0,0,0,0.015) 2px,
      rgba(0,0,0,0.015) 4px
    );
  }

  /* PULL QUOTE */
  .pull-quote {
    border-top: 2px solid var(--ink);
    border-bottom: 2px solid var(--ink);
    padding: 8px 14px;
    margin: 8px 0;
    text-align: center;
  }

  .pull-quote p {
    font-size: 11.5px;
    font-style: italic;
    font-weight: 600;
    line-height: 1.5;
    color: var(--red);
  }

  /* FEATURED BOX */
  .box {
    background: var(--paper-aged);
    border: 1px solid var(--border);
    padding: 8px 10px;
    margin: 7px 0;
  }

  .box-title {
    font-size: 11px;
    font-weight: 700;
    color: var(--red);
    margin-bottom: 4px;
  }

  .box-body {
    font-size: 9.5px;
    line-height: 1.58;
    color: var(--ink);
  }

  /* STAT */
  .stat {
    border: 2px solid var(--red);
    padding: 6px 8px;
    margin: 6px 0;
    text-align: center;
  }

  .stat-num {
    font-family: 'Playfair Display', serif;
    font-size: 28px;
    font-weight: 900;
    color: var(--red);
    line-height: 1;
  }

  .stat-lbl { font-size: 9px; color: var(--ink-light); margin-top: 2px; }

  /* 2-COL TEXT */
  .two-col {
    column-count: 2;
    column-gap: 14px;
    column-rule: 1px solid var(--rule);
    font-size: 10.5px;
    line-height: 1.65;
    text-align: justify;
  }

  /* LEAD */
  .lead {
    font-size: 11px;
    font-weight: 600;
    line-height: 1.55;
    border-left: 3px solid var(--red);
    padding-left: 8px;
    margin-bottom: 6px;
    color: var(--ink-mid);
  }

  /* RASHIFAL */
  .rashi-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 5px;
    margin-top: 6px;
  }

  .rashi-card {
    border: 1px solid var(--rule);
    padding: 6px 8px;
    background: var(--paper-aged);
    transition: transform 0.15s;
    cursor: default;
  }

  .rashi-card:hover { transform: translateY(-1px); box-shadow: 0 2px 6px rgba(0,0,0,0.12); }

  .rashi-name {
    font-size: 10px;
    font-weight: 700;
    color: var(--red);
    border-bottom: 1px solid var(--rule);
    padding-bottom: 2px;
    margin-bottom: 3px;
  }

  .rashi-text { font-size: 9.5px; line-height: 1.55; color: var(--ink); }
  .rashi-color { font-size: 8.5px; color: var(--gold); margin-top: 3px; font-style: italic; }

  /* PAGE 2 EDITORIAL */
  .editorial-block {
    background: var(--paper);
    border-left: 4px solid var(--red);
    padding: 0 0 0 12px;
  }

  /* LIST ITEMS NEWS */
  .news-list { list-style: none; }
  .news-list li {
    padding: 5px 0;
    border-bottom: 1px solid var(--paper-dark);
    font-size: 10.5px;
    line-height: 1.55;
    color: var(--ink);
  }
  .news-list li:last-child { border-bottom: none; }
  .news-list li::before {
    content: '◆ ';
    color: var(--red);
    font-size: 8px;
  }

  /* FOOTER */
  .footer {
    border-top: 3px double var(--border);
    padding: 7px 20px;
    background: var(--paper-aged);
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 9px;
    color: var(--ink-light);
  }

  .page-num {
    font-family: 'Playfair Display', serif;
    font-size: 22px;
    font-weight: 900;
    color: var(--ink);
    width: 36px;
    height: 36px;
    border: 2px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .email-bar {
    border-top: 1px solid var(--rule);
    text-align: right;
    font-size: 9px;
    color: var(--gold);
    font-style: italic;
    padding: 3px 20px;
  }

  @media (max-width: 700px) {
    .paper-title { font-size: 36px; }
    .grid-6, .grid-5, .grid-4, .grid-3 { grid-template-columns: 1fr 1fr; }
    .col-3, .col-4, .col-2 { grid-column: span 2; }
    .rashi-grid { grid-template-columns: repeat(2, 1fr); }
  }
`;

const newsData = {
  page1: {
    mainStory: {
      tag: "प्रमुख समाचार",
      headline: "अमित शाह बोले— नेहरू की दो गलतियों का कश्मीर ने कई सालों तक भुगता खामियाजा",
      byline: "एजेंसी | नई दिल्ली",
      body: [
        "अमित शाह ने कहा कि दो बड़ी गलतियां नेहरू के कार्यकाल में हुईं। पहली और सबसे बड़ी गलती — जब हमारी सेना जीत रही थी, पंजाब का क्षेत्र आते ही संघर्ष विराम कर दिया गया और पीओके का जन्म हुआ।",
        "संसद में जम्मू कश्मीर से जुड़े दो विधेयकों पर हुई चर्चा का जवाब देते हुए अमित शाह ने कहा कि पाक अधिकृत कश्मीर भारत का है। जम्मू में पहले 37 सीटें थीं, अब 43 हैं। कश्मीर में पहले 46 थीं, अब 47 हैं और PoK में 24 सीटें हमने रिजर्व रखी हैं।",
        "शाह ने कहा, \"मेरा मानना है कि इस मामले को संयुक्त राष्ट्र में नहीं ले जाना चाहिए था, लेकिन अगर ले जाना था तो चार्टर 51 के तहत ले जाना चाहिए था, लेकिन चार्टर 35 के तहत ले जाया गया।\"",
      ]
    },
    mpStory: {
      tag: "मध्य प्रदेश",
      headline: "एमपी-राजस्थान और छत्तीसगढ़ में किसे मिलेगी कमान? सामने आया पीएम मोदी का पहला प्लान",
      body: [
        "मध्य प्रदेश, छत्तीसगढ़ और राजस्थान के नतीजे आए हुए 3 दिन हो चुके हैं। लेकिन भाजपा ने अभी तक इन राज्यों में मुख्यमंत्री पद को लेकर खुलासा नहीं किया है।",
        "भाजपा के 12 सांसदों में से 10 ने बुधवार को संसद की सदस्यता से इस्तीफा दे दिया। इस्तीफा देने वाले में केंद्रीय मंत्री नरेंद्र सिंह तोमर और प्रह्लाद पटेल शामिल हैं।",
      ]
    },
    adrStory: {
      tag: "विशेष रिपोर्ट",
      headline: "230 में से 90 नवनिर्वाचित विधायकों पर आपराधिक मामले, 89% करोड़पति",
      body: [
        "ADR की रिपोर्ट — 51 भाजपा के, 38 कांग्रेस के और 1 भारतीय आदिवासी पार्टी के विधायक आपराधिक मामलों वाले।",
        "रतलाम के विधायक चेतन्य कश्यप 296 करोड़ की संपत्ति के साथ सबसे अमीर विधायक।",
      ]
    }
  },
  page2: {
    editorial: {
      tag: "सम्पादकीय",
      headline: "विपक्ष के लिए विचारणीय — छत्तीसगढ़ में 'शकुनि के पांसों से खेलने की डेढ़ होशियारी' ले डूबी कांग्रेस",
      body: [
        "बेशक, आज भाजपा के पास अकूत संसाधन हैं। लेकिन उसकी जीत का सिर्फ यही कारण नहीं है। बल्कि वह अपनी हिंदुत्व की राजनीति के पक्ष में इतनी ठोस गोलबंदी कर चुकी है कि चुनावों में उसे परास्त करना अति कठिन हो गया है।",
        "चार राज्यों के विधानसभा चुनावों के नतीजों का केंद्रीय संदेश यह है कि विपक्ष के पास भारतीय जनता पार्टी की राजनीति की कोई काट नहीं है।",
        "ओबीसी अस्मिता का उभार अब गुजरे जमाने की बात हो चुका है। इस बीच खुद ओबीसी के अंदर विभिन्न जातीय अंतर्विरोध तीखे रूप ले चुके हैं।",
        "तीन हिंदी भाषी राज्यों के चुनाव नतीजों से साफ है कि जातीय अस्मिता को अपनी चुनावी रणनीति का आधार बनाने की कांग्रेस की रणनीति उसे बहुत महंगी पड़ी।",
      ]
    },
    socialTruth: {
      tag: "आज का सामाजिक सच",
      headline: "कांग्रेस बैठाती है मैनेजरों को सिर पर!",
      body: [
        "उत्तर भारत में क्यों कर कांग्रेस डूबी — किंवदंती की तरह दिल्ली के राजनीतिक जानकारों में चर्चा रही है कि 2014 में लोकसभा चुनाव में नरेंद्र मोदी चुनाव जीत गए तो उनके सलाहकार मैनेजर प्रशांत किशोर ने अपना भविष्य जानने के लिए अमित शाह से बात की।",
        "बेशक प्रशांत किशोर चुनाव प्रबंधन विधा में भारत के नंबरो उनो हैं। लेकिन मोदी-शाह और भाजपा के सेटअप में उनकी कोई जगह नहीं बनी।",
      ]
    }
  },
  page3: {
    ambedkar: {
      tag: "उत्तर प्रदेश",
      headline: "बाबा आंबेडकर बिना झुके व बिना डिगे भारत और भारतीयता के लिए कार्य किया — मुख्यमंत्री",
      byline: "संवाददाता | लखनऊ",
      body: [
        "मुख्यमंत्री योगी आदित्यनाथ ने कहा है कि दुनिया में जहां कहीं भी जब किसी दबे-कुचले समाज की आवाज को बुलंद करने की बात आती है, तो लोगों के मन में बाबा साहब डॉ. भीमराव आंबेडकर का नाम अनायास ही आ जाता है।",
        "बाबा साहब भारतीय संविधान के शिल्पी थे। प्रत्येक काम, देश के नाम उनका सपना था। उन्होंने बिना झुके व बिना डिगे भारत और भारतीयता के लिए कार्य किया। यही कारण है कि पूरे देश में बड़ी संख्या में बाबा साहब डॉ. आंबेडकर से जुड़े स्मारक हैं।",
        "प्रदेश सरकार प्रत्येक गरीब, दलित और वंचित के साथ खड़ी है। आज गरीबों और दलितों को प्रत्येक क्षेत्र में आरक्षण की सुविधा मिल रही है।",
      ]
    },
    homeguard: {
      tag: "लखनऊ",
      headline: "होमगार्ड जवान प्रशासनिक ड्यूटी के साथ सामाजिक कार्यों में भी निभा रहे हैं अपनी महत्वपूर्ण भूमिका",
      byline: "संवाददाता | लखनऊ",
      body: [
        "होमगार्ड्स संगठन स्थापना के 62वें स्थापना दिवस समारोह में उत्तर प्रदेश के कारागार एवं होमगार्ड्स राज्यमंत्री धर्मवीर प्रजापति ने रैंकिक परेड का मान-प्रणाम स्वीकार किया।",
        "2017 से प्रदेश सरकार में योगी जी के नेतृत्व में होमगार्ड जवानों के लिए बेहतरी के लिए बहुत से कार्य किए गए हैं। होमगार्ड जवानों का दैनिक भत्ता पहले 375 रुपये था जिसे दो बार में बढ़ाते हुए 600 रुपये कर दिया गया है।",
      ]
    },
    britain: {
      tag: "अंतर्राष्ट्रीय",
      headline: "भारत में ब्रिटेन के उच्चायुक्त एडम टेलर के साथ जयशंकर और रम्या कपूर ने की मुलाकात",
      byline: "संवाददाता | लखनऊ",
      body: [
        "उत्तर प्रदेश के सहकारिता जेपीएस राठौर से भारत में ब्रिटेन के उच्चायुक्त एडम टेलर के साथ हेड ऑफ इकोनॉमिक्स जयशंकर और रम्या कपूर ने आज उत्तर प्रदेश राज्य भंडारण निगम कार्यालय में मुलाकात की।",
        "उत्तर प्रदेश में रेल, सड़क व एयर कनेक्टिविटी बेहतर है। यहां पानी, भूमि, बिजली, स्किल लेबर, उच्च तकनीकी संस्थान उपलब्ध है।",
      ]
    }
  },
  page4: {
    kabaddi: {
      tag: "खेल समाचार",
      headline: "नमो कबड्डी प्रतियोगिता का हुआ भव्य शुभारंभ — खिलाड़ियों को अच्छा अवसर देगा नमो कबड्डी प्रतियोगिता",
      byline: "ब्यूरो | देवरिया (उ.प्र.)",
      body: [
        "देवरिया जिले में भाजपा किसान मोर्चा द्वारा आयोजित नमो कबड्डी प्रतियोगिता का शुभारंभ विधायक डॉ. शलभ मणि त्रिपाठी, नगर पालिका अध्यक्ष अलका सिंह तथा किसान मोर्चा के प्रभारी राधेश्याम पांडेय ने दीप प्रज्वलित कर किया।",
        "विधायक शलभ मणि ने कहा कि भाजपा सरकार खेल और खिलाड़ियों को हर प्रकार से प्रोत्साहित कर रही है। मोदी-योगी सरकार की मंशा है गांव के खिलाड़ियों को एक अलग पहचान देना।",
        "भाटपाररानी, गौरीबाजार, कोरवा और भटनी अगले राउंड में भाजपा किसान मोर्चा द्वारा आयोजित नमो कबड्डी प्रतियोगिता का उद्घाटन।",
      ]
    },
    thana: {
      tag: "देवरिया",
      headline: "प्रधानसंघ के जिलाध्यक्ष के हत्या मामले में दो और गिरफ्तार",
      byline: "ब्यूरो | देवरिया (उ.प्र.)",
      body: [
        "बीते 11 अक्टूबर को जिलाध्यक्ष संघ के हत्या के मामले में देवरिया की कोतवाली पुलिस ने दो और लोगों को गिरफ्तार कर जेल भेज दिया।",
        "इस मामले में पुलिस इससे पहले पिकअप वाहन चालक व उसके सहायक को गिरफ्तार कर जेल भेज चुकी है।",
      ]
    },
    rashtriya: {
      tag: "बांदा",
      headline: "लोगों को समृद्ध बनाने से पूरा होगा विकसित देश का संकल्प",
      body: [
        "वर्ष 2047 से पहले भारत को विकसित और समृद्ध देश बनाने के संकल्प के साथ प्रशासन और संगठन के स्तर पर विकसित भारत संकल्प यात्रा का आयोजन किया जा रहा है।",
        "भाजपा के बड़ोखर मंडल अध्यक्ष श्यामबाबू पाल ने कार्यक्रम का शुभारंभ करते केंद्र व प्रदेश सरकार की उपलब्धियों का जमकर बखान किया।",
      ]
    }
  }
};

const rashiData = [
  { sign: "♈ मेष", text: "सरकारी संस्थान से जुड़े लोगों का दिन अच्छा। प्रमोशन की संभावना। करीबी लोगों की राय मानना लाभदायक।", color: "शुभ रंग: भूरा | अंक: 7" },
  { sign: "♉ वृष", text: "जीवनसाथी के साथ घूमने की योजना। व्यापार में बड़ी सफलता। पुरानी जायदाद के लेन-देन में धन लाभ।", color: "शुभ रंग: गुलाबी | अंक: 6" },
  { sign: "♊ मिथुन", text: "प्रॉपर्टी में निवेश के योग। दोस्त की मदद से अच्छी कंपनी में नौकरी के योग। घर की सुख-सुविधाओं में बढ़ोतरी।", color: "शुभ रंग: हरा | अंक: 4" },
  { sign: "♋ कर्क", text: "पिता का जरूरी काम पूरा करने में सफल। होटल-रेस्तरां व्यवसाय करने वालों के लिए दिन बढ़िया।", color: "शुभ रंग: मैजेंटा | अंक: 4" },
  { sign: "♌ सिंह", text: "नई जिम्मेदारियां आ सकती हैं। मांगलिक कार्यक्रम की तैयारी में जुटे हैं तो आस-पड़ोस के लोग खुश होंगे।", color: "शुभ रंग: पीच | अंक: 6" },
  { sign: "♍ कन्या", text: "धार्मिक कार्य में रुचि। ऑफिस का कोई व्यक्ति आपके घर पर आ सकता है। बूटिक का काम लकी।", color: "शुभ रंग: नीला | अंक: 3" },
];

export default function IndiaJunctionEpaper() {
  const [activePage, setActivePage] = useState(1);

  return (
    <>
      <style>{styles}</style>
      <div className="epaper-wrapper">
        <div className="newspaper">

          {/* TOP STRIP */}
          <div className="top-strip">
            <span>UPHIN/2014/57301</span>
            <span>हिन्दी दैनिक समाचार-पत्र</span>
            <span>indiajunctionnews@gmail.com</span>
          </div>

          {/* MASTHEAD */}
          <div className="masthead">
            <div className="masthead-inner">
              <div className="masthead-left">
                <div><strong>वर्ष</strong> : 10</div>
                <div><strong>अंक</strong> : 222</div>
                <div><strong>UPHIN/2014/57301</strong></div>
                <div>लखनऊ से प्रकाशित</div>
              </div>
              <div className="masthead-center">
                <div className="paper-title">इण्डिया जंक्शन</div>
                <span className="paper-hindi">हिन्दी दैनिक समाचार-पत्र</span>
                <div className="paper-tagline">लखनऊ से प्रकाशित</div>
              </div>
              <div className="masthead-right">
                <div><strong>लखनऊ, रविवार</strong></div>
                <div><strong>10 दिसम्बर 2023</strong></div>
                <div>पृष्ठ : 04</div>
                <div>मूल्य : ₹ 3.00</div>
              </div>
            </div>
          </div>

          {/* EDITION BAR */}
          <div className="edition-bar">
            <span>रविवार, 10 दिसम्बर 2023 | लखनऊ संस्करण</span>
            <span>पृष्ठ 1 से 4</span>
            <span className="price-tag">₹ 03</span>
          </div>

          {/* TICKER */}
          <div className="breaking-bar">
            <span className="ticker-content">
              ◆ अमित शाह — नेहरू की दो गलतियों का कश्मीर ने वर्षों तक भुगता खामियाजा &nbsp;&nbsp;&nbsp; ◆ एमपी-राजस्थान-छत्तीसगढ़ में CM पर पीएम मोदी लेंगे आखिरी फैसला &nbsp;&nbsp;&nbsp; ◆ चेन्नई में बाढ़, लगातार तीसरे दिन बिजली नहीं, सैकड़ों लोग राहत शिविरों में &nbsp;&nbsp;&nbsp; ◆ 230 में से 90 नवनिर्वाचित विधायकों पर आपराधिक मामले — ADR रिपोर्ट &nbsp;&nbsp;&nbsp;
            </span>
          </div>

          {/* PAGE TABS */}
          <div className="page-tabs">
            {[1,2,3,4].map(p => (
              <button
                key={p}
                className={`page-tab ${activePage === p ? 'active' : ''}`}
                onClick={() => setActivePage(p)}
              >
                पृष्ठ {p}
              </button>
            ))}
          </div>

          {/* ===================== PAGE 1 ===================== */}
          {activePage === 1 && (
            <div className="content">
              <div className="grid-6">

                {/* MAIN: Amit Shah */}
                <div className="article col-3" style={{borderLeft:'none', paddingLeft:4}}>
                  <span className="section-label">{newsData.page1.mainStory.tag}</span>
                  <h2 className="hl hl-xl hl-red">{newsData.page1.mainStory.headline}</h2>
                  <div className="byline">{newsData.page1.mainStory.byline}</div>
                  <div className="photo" style={{height:88}}>
                    <span style={{fontSize:22, opacity:0.35}}>🏛️</span>
                    <span>केंद्रीय गृह मंत्री अमित शाह — संसद में भाषण | 9 दिसंबर 2023</span>
                  </div>
                  <div className="body drop-cap">
                    {newsData.page1.mainStory.body.map((p,i) => <p key={i}>{p}</p>)}
                  </div>
                  <div className="pull-quote">
                    <p>"PoK के बिना जम्मू-कश्मीर की बात अधूरी है। पाक अधिकृत कश्मीर भारत का है और भारत का हिस्सा रहेगा।"</p>
                  </div>
                  <div className="box">
                    <div className="box-title">जम्मू-कश्मीर विधानसभा में सीटें</div>
                    <div className="box-body">
                      जम्मू में पहले 37 → अब <strong>43</strong> सीटें &nbsp;|&nbsp;
                      कश्मीर में पहले 46 → अब <strong>47</strong> सीटें &nbsp;|&nbsp;
                      PoK की <strong>24</strong> सीटें आरक्षित
                    </div>
                  </div>
                </div>

                {/* MP/Rajasthan */}
                <div className="article col-2">
                  <span className="section-label">{newsData.page1.mpStory.tag}</span>
                  <h2 className="hl hl-lg">{newsData.page1.mpStory.headline}</h2>
                  <div className="body">
                    {newsData.page1.mpStory.body.map((p,i) => <p key={i}>{p}</p>)}
                  </div>
                  <div className="box" style={{marginTop:8}}>
                    <div className="box-title">🏆 राजस्थान CM दौड़ में नाम</div>
                    <div className="box-body" style={{fontSize:'9.5px', lineHeight:1.65}}>
                      वसुंधरा राजे सिंधिया, दीया कुमारी, गजेंद्र सिंह शेखावत, बाबा बालकनाथ, अर्जुन राम मेघवाल समेत करीब एक दर्जन नाम चर्चा में
                    </div>
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">मध्य प्रदेश</span>
                  <h2 className="hl hl-md">मध्य प्रदेश में भाजपा का मिशन-29 का आगाज, मेगा जीत के बाद शिवराज फिर मैदान में</h2>
                  <div className="body">
                    <p>मध्य प्रदेश में मिली बम्पर जीत के बाद मुख्यमंत्री शिवराज सिंह चौहान छिंदवाड़ा पहुंचे। "हम 29 लोकसभा सीटें जीतेंगे। भाजपा को 48.6% वोट मिले हैं जो इतिहास में कभी नहीं मिले।"</p>
                  </div>
                </div>

                {/* ADR Report */}
                <div className="article">
                  <span className="section-label">{newsData.page1.adrStory.tag}</span>
                  <h2 className="hl hl-md">{newsData.page1.adrStory.headline}</h2>
                  <div className="byline">एजेंसी | नई दिल्ली</div>
                  <div className="stat"><div className="stat-num">90</div><div className="stat-lbl">विधायक आपराधिक मामलों वाले</div></div>
                  <div className="stat"><div className="stat-num">89%</div><div className="stat-lbl">नवनिर्वाचित विधायक करोड़पति</div></div>
                  <div className="stat"><div className="stat-num">205</div><div className="stat-lbl">विधायक करोड़पति (230 में से)</div></div>
                  <div className="body" style={{marginTop:7}}>
                    {newsData.page1.adrStory.body.map((p,i) => <p key={i}>{p}</p>)}
                  </div>
                </div>

              </div>

              <hr className="rule" />

              {/* ROW 2 */}
              <div className="grid-4">
                <div className="article" style={{borderLeft:'none', paddingLeft:4}}>
                  <span className="section-label">आपदा समाचार</span>
                  <h2 className="hl hl-md">चेन्नई में अब भी बाढ़, लगातार तीसरे दिन कई क्षेत्रों में बिजली नहीं, सैकड़ों लोग राहत शिविरों में</h2>
                  <div className="photo" style={{height:72, marginBottom:6}}>
                    <span style={{fontSize:20, opacity:0.35}}>🌊</span>
                    <span>चेन्नई बाढ़ — राहत कार्य जारी</span>
                  </div>
                  <div className="body">
                    <p>चक्रवात मिचौंग के बाद से चेन्नई जूझ रहा है। कोलाथुर में दुकानदार राजाराम ने बताया कि 30 से अधिक दुकानों में पानी घुस गया है। चीन से आयातित 1,000 से अधिक मछलियां मर गईं।</p>
                    <p>CM स्टालिन ने केंद्र को पत्र लिखकर 5,060 करोड़ रुपये की अंतरिम राहत मांगी।</p>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">लखनऊ</span>
                  <h2 className="hl hl-md">डॉ. भीमराव अम्बेडकर के परिनिर्वाण दिवस पर पुलिस मुख्यालय में पुष्पांजलि सभा का आयोजन</h2>
                  <div className="byline">संवाददाता | लखनऊ</div>
                  <div className="body">
                    <p>भारतरत्न बाबा साहब डॉ. भीमराव अम्बेडकर के परिनिर्वाण दिवस के अवसर पर पुलिस मुख्यालय गोमतीनगर, लखनऊ विस्तार में पुष्पांजलि सभा का आयोजन किया गया।</p>
                    <p>पुलिस महानिदेशक विजय कुमार ने बाबा साहब के प्रतिपादित सिद्धांतों एवं मूल्यों पर अपने विचार रखे।</p>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">उत्तर प्रदेश</span>
                  <h2 className="hl hl-md">सीएम योगी — जाति के नाम पर बांटने वाले आंबेडकर विरोधी, डिप्टी सीएम बोले — कांग्रेस ने बाबा साहब को चुनाव हरवाया</h2>
                  <div className="byline">संवाददाता | लखनऊ</div>
                  <div className="body">
                    <p>मुख्यमंत्री योगी आदित्यनाथ ने कहा कि जो लोग भारत का विरोध करते हैं वह बाबा साहब डॉ. भीमराव आंबेडकर का अपमान करते हैं।</p>
                    <p>"जो लोग जातीयता के नाम पर समाज में खाई उत्पन्न करने का काम करते हैं, वे सही मायने में बाबा साहब का अपमान करते हैं।"</p>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">महाराष्ट्र</span>
                  <h2 className="hl hl-md">'शासन आपल्या दारी' अभियान के माध्यम से 1.84 करोड़ लाभार्थियों को विभिन्न योजनाओं का सीधा लाभ</h2>
                  <div className="byline">एजेंसी | नई दिल्ली</div>
                  <div className="body">
                    <p>महाराष्ट्र राज्य की प्रमुख योजना 'शासन आपल्या दारी' अभियान के माध्यम से 1 करोड़ 84 लाख लाभार्थियों को विभिन्न योजनाओं से सीधे लाभ मिला है।</p>
                    <p>मुख्यमंत्री एकनाथ शिंदे ने बीड जिले के परली तहसील के ओपले मैदान में यह जानकारी दी।</p>
                  </div>
                </div>
              </div>

              <hr className="rule" />

              {/* RASHIFAL */}
              <div>
                <span className="section-label">आज का राशिफल — 10 दिसम्बर 2023</span>
                <div className="rashi-grid">
                  {rashiData.map((r, i) => (
                    <div key={i} className="rashi-card">
                      <div className="rashi-name">{r.sign}</div>
                      <div className="rashi-text">{r.text}</div>
                      <div className="rashi-color">{r.color}</div>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          )}

          {/* ===================== PAGE 2 ===================== */}
          {activePage === 2 && (
            <div className="content">
              <div className="grid-3">

                <div className="article col-2" style={{borderLeft:'none', paddingLeft:4}}>
                  <span className="section-label">{newsData.page2.editorial.tag}</span>
                  <h2 className="hl hl-lg hl-red">{newsData.page2.editorial.headline}</h2>
                  <div className="lead">बेशक, आज भाजपा के पास अकूत संसाधन हैं। लेकिन उसकी जीत का सिर्फ यही कारण नहीं है।</div>
                  <div className="two-col body">
                    {newsData.page2.editorial.body.map((p, i) => <p key={i}>{p}</p>)}
                    <p>ओबीसी गोलबंदी की बात करने पर टकराव सिर्फ सवर्ण जातियों से नहीं उभरता, बल्कि "बहुजन" की जो धारणा एक समय पेश की गई थी, उसमें शामिल कई अन्य समूहों के साथ भी अंतर्विरोध खड़ा हो जाता है।</p>
                    <p>छत्तीसगढ़ में फिर से सत्ता में आने का कांग्रेस का सपना चकनाचूर हुआ है, तो कांग्रेस की हार के कारण स्पष्ट हैं। सत्ता का घमंड जब सिर चढ़कर बोलता है, तो वह अपनों के साथ जनता को भी दूर करता है।</p>
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">{newsData.page2.socialTruth.tag}</span>
                  <h2 className="hl hl-md">{newsData.page2.socialTruth.headline}</h2>
                  <div className="two-col body">
                    {newsData.page2.socialTruth.body.map((p, i) => <p key={i}>{p}</p>)}
                    <p>बेशक प्रशांत किशोर चुनाव प्रबंधन विधा में भारत के नंबरो उनो हैं। गुरु हैं। लेकिन मोदी-शाह और भाजपा के सेटअप में, उनकी राजनीतिक व्यवस्था में चुनाव का काम खत्म होने के बाद प्रशांत किशोर को दूध में मक्खी की तरह बाहर फेंकने की वास्तविकता बहुत कुछ बताती है।</p>
                    <p>इसके उलट कांग्रेस प्रशांत किशोर की लीक पर बने चुनावी मैनेजरों को ठेके देती है, उन्हें सिर पर बैठाती है और उन पर निर्भर होकर एक के बाद एक चुनाव हार रही है।</p>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">राजस्थान</span>
                  <h2 className="hl hl-md">सत्ता के लिए शक्ति प्रदर्शन में जुटी वसुंधरा राजे से आगे कई नाम नजर आ रहे हैं</h2>
                  <div className="photo" style={{height:100, marginBottom:7}}>
                    <span style={{fontSize:22, opacity:0.35}}>🏛️</span>
                    <span>जयपुर — भाजपा विधायकों की बैठक</span>
                  </div>
                  <div className="body">
                    <p>जयपुर में वसुंधरा राजे द्वारा भाजपा विधायकों से फेस टू फेस बातचीत के घटनाक्रम पर भाजपा आलाकमान पूरी नजर रखे हुए है।</p>
                    <p>चर्चा है कि वसुंधरा राजे से अब तक करीब 30 विधायकों की मुलाकात हो चुकी है। वैर से विधायक बने बहादुर सिंह कोली व नसीराबाद से जीते रामस्वरूप लांबा ने खुलकर वसुंधरा राज को मुख्यमंत्री बनाने की मांग की है।</p>
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">विशेष</span>
                  <h2 className="hl hl-sm">CM रेस में वसुंधरा के अलावा ये नाम भी चर्चा में</h2>
                  <ul className="news-list" style={{marginTop:5}}>
                    {["दीया कुमारी (विद्याधर नगर)","गजेंद्र सिंह शेखावत (जल शक्ति मंत्री)","बाबा बालकनाथ","अर्जुन राम मेघवाल (कानून राज्य मंत्री)","रायपुर (पश्चिम) से विधायक","यूनुस खान (निर्दलीय विजेता)"].map((n, i) => (
                      <li key={i}>{n}</li>
                    ))}
                  </ul>
                </div>

              </div>
            </div>
          )}

          {/* ===================== PAGE 3 ===================== */}
          {activePage === 3 && (
            <div className="content">
              <div className="grid-3">

                <div className="article col-2" style={{borderLeft:'none', paddingLeft:4}}>
                  <span className="section-label">{newsData.page3.ambedkar.tag}</span>
                  <h2 className="hl hl-lg hl-red">{newsData.page3.ambedkar.headline}</h2>
                  <div className="byline">{newsData.page3.ambedkar.byline}</div>
                  <div className="grid-2" style={{gap:10, marginBottom:8}}>
                    <div className="photo" style={{height:90}}>
                      <span style={{fontSize:22, opacity:0.35}}>🙏</span>
                      <span>CM योगी — अम्बेडकर महासभा में श्रद्धांजलि</span>
                    </div>
                    <div className="photo" style={{height:90}}>
                      <span style={{fontSize:22, opacity:0.35}}>📜</span>
                      <span>बाबा साहब डॉ. भीमराव अम्बेडकर</span>
                    </div>
                  </div>
                  <div className="two-col body">
                    {newsData.page3.ambedkar.body.map((p, i) => <p key={i}>{p}</p>)}
                    <p>प्रदेश सरकार द्वारा श्रमिकों के बच्चों के लिए अटल आवासीय विद्यालय प्रारंभ किए गए हैं। यह पब्लिक और कॉन्वेंट स्कूल से बेहतर सुविधाओं से युक्त हैं।</p>
                    <p>वर्ष 2014 से पहले आवास एक सपना था। PM जी के नेतृत्व में आज देश में 04 करोड़ परिवारों को पक्के आवास उपलब्ध कराए जा चुके हैं।</p>
                    <p>मैला ढोने की कुप्रथा को बंद करने के विकल्प के रूप में 12 करोड़ शौचालयों का निर्माण कराया गया।</p>
                  </div>

                  <hr className="rule-thin" />

                  <div className="grid-2" style={{gap:12}}>
                    <div>
                      <span className="section-label">लखनऊ</span>
                      <h2 className="hl hl-sm">संस्कृत संस्थान परिसर में बाबा साहब के महापरिनिर्वाण दिवस पर व्याख्यान गोष्ठी</h2>
                      <div className="body">
                        <p>उत्तर प्रदेश संस्कृत संस्थान एवं उत्तर प्रदेश भाषा संस्थान द्वारा आज संस्कृत संस्थान परिसर में "डॉ. भीमराव अम्बेडकर का राष्ट्र निर्माण में योगदान" विषय पर व्याख्यान गोष्ठी का आयोजन।</p>
                      </div>
                    </div>
                    <div>
                      <span className="section-label">समाजवादी पार्टी</span>
                      <h2 className="hl hl-sm">डॉ. अम्बेडकर ने लड़ी सामाजिक न्याय की लड़ाई — अखिलेश यादव</h2>
                      <div className="body">
                        <p>समाजवादी पार्टी के राष्ट्रीय अध्यक्ष ने बाबा साहब डॉ. भीमराव अम्बेडकर के चित्र पर माल्यार्पण कर श्रद्धांजलि अर्पित की। अखिलेश यादव ने कहा कि बाबा साहब को भारतीय संविधान के निर्माता एवं महान समाज सुधारक के रूप में हमेशा याद किया जाएगा।</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">{newsData.page3.homeguard.tag}</span>
                  <h2 className="hl hl-md">{newsData.page3.homeguard.headline}</h2>
                  <div className="byline">{newsData.page3.homeguard.byline}</div>
                  <div className="photo" style={{height:85, marginBottom:7}}>
                    <span style={{fontSize:22, opacity:0.35}}>🎖️</span>
                    <span>होमगार्ड्स 62वां स्थापना दिवस — लखनऊ</span>
                  </div>
                  <div className="body">
                    {newsData.page3.homeguard.body.map((p, i) => <p key={i}>{p}</p>)}
                    <p>आज सॉफ्टवेयर से उनकी ड्यूटी लगाई जा रही है। किसी भी अनियमितता की गुंजाइश ही नहीं रहती।</p>
                  </div>

                  <hr className="rule-thin" />

                  <span className="section-label">{newsData.page3.britain.tag}</span>
                  <h2 className="hl hl-sm">{newsData.page3.britain.headline}</h2>
                  <div className="byline">{newsData.page3.britain.byline}</div>
                  <div className="body">
                    {newsData.page3.britain.body.map((p, i) => <p key={i}>{p}</p>)}
                  </div>

                  <hr className="rule-thin" />

                  <span className="section-label">नागरिक सुरक्षा</span>
                  <h2 className="hl hl-sm">नागरिक सुरक्षा संगठन के स्थापना दिवस पर 22 लोगों ने किया रक्तदान</h2>
                  <div className="body">
                    <p>नागरिक सुरक्षा संगठन का स्थापना दिवस नागरिक सुरक्षा के कंट्रोल रूम में मनाया गया। DG नागरिक सुरक्षा मुकुल गोयल ने सर्वप्रथम नागरिक सुरक्षा का ध्वज तोलन किया। इसके बाद 22 स्वयंसेवकों ने रक्तदान किया।</p>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* ===================== PAGE 4 ===================== */}
          {activePage === 4 && (
            <div className="content">
              <div className="grid-4">

                <div className="article col-2" style={{borderLeft:'none', paddingLeft:4}}>
                  <span className="section-label">{newsData.page4.kabaddi.tag}</span>
                  <h2 className="hl hl-lg hl-red">{newsData.page4.kabaddi.headline}</h2>
                  <div className="byline">{newsData.page4.kabaddi.byline}</div>
                  <div className="photo" style={{height:85, marginBottom:7}}>
                    <span style={{fontSize:22, opacity:0.35}}>🏅</span>
                    <span>नमो कबड्डी प्रतियोगिता — देवरिया</span>
                  </div>
                  <div className="two-col body">
                    {newsData.page4.kabaddi.body.map((p, i) => <p key={i}>{p}</p>)}
                    <p>भाटपाररानी और गौरीबाजार के बीच हुए दूसरे मैच में गौरीबाजार ने 30-19 के अंतर से मैच अपने पक्ष में करने में सफल रही। तीसरे मैच में पिपरा चंद्रभान और कोरवा-रुद्रपुर की टीम भिड़ी जिसमें कोरवा ने पिपरा चंद्रभान को 44-17 के अंतर से पटखनी दी।</p>
                  </div>

                  <hr className="rule-thin" />

                  <div className="grid-2" style={{gap:10}}>
                    <div>
                      <span className="section-label">देवरिया</span>
                      <h2 className="hl hl-sm">गरीबों और असहायों के साथ खड़ी है प्रदेश सरकार — राज्य मंत्री</h2>
                      <div className="body">
                        <p>देवरिया जिले में माननीय राज्य मंत्री राजस्व विभाग अनूप प्रधान ने आज कलेक्ट्रेट सभागार में राजस्व व चकबंदी विभाग के कार्यों की समीक्षा की।</p>
                        <p>माननीय राज्य मंत्री ने कहा कि योगी सरकार गरीबों और असहायों के साथ खड़ी है। भू-माफियाओं को चिन्हित कर उनके विरुद्ध सख्त कार्रवाई सुनिश्चित की जा रही है।</p>
                      </div>
                    </div>
                    <div>
                      <span className="section-label">बांदा</span>
                      <h2 className="hl hl-sm">राज्यमंत्री और DM ने जल ज्ञान यात्रा को दिखाई हरी झंडी</h2>
                      <div className="body">
                        <p>जलशक्ति राज्यमंत्री रामकेश निषाद और जिलाधिकारी दुर्गा शक्ति नागपाल ने बुधवार को GIC ग्राउंड से जल जीवन मिशन से स्कूली बच्चों को जोड़ने की अनूठी पहल के तहत जल ज्ञान यात्रा को हरी झंडी दिखाई।</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">{newsData.page4.thana.tag}</span>
                  <h2 className="hl hl-md">{newsData.page4.thana.headline}</h2>
                  <div className="byline">{newsData.page4.thana.byline}</div>
                  <div className="body">
                    {newsData.page4.thana.body.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                  <div className="box" style={{marginTop:8}}>
                    <div className="box-title">गिरफ्तार अभियुक्त</div>
                    <div className="box-body">
                      1. पूर्व BDC आबिद खान (निवासी सोनूघाट टोला परसिया मेंज)<br/>
                      2. अजय तिवारी पु. रुपौरी शरण तिवारी (निवासी परासिया मेंज)
                    </div>
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">देवरिया</span>
                  <h2 className="hl hl-sm">पुलिस के मुखबिरों द्वारा कई चोरी के मामलों में वंचित अभियुक्तों की हुई गिरफ्तारी</h2>
                  <div className="body">
                    <p>देवरिया जिले में श्रीमान पुलिस अधीक्षक महोदय जनपद देवरिया द्वारा अपराध एवं अपराधियों पर नियंत्रण हेतु चलाए जा रहे अभियान के अंतर्गत थाना कोतवाली जनपद देवरिया के उ.नि. हरिनाथ सिंह यादव व उ.नि. संजय सिंह चंदेल मय हमराहियान द्वारा कई चोरी के मामलों में 5 अभियुक्त गिरफ्तार।</p>
                  </div>
                </div>

                <div className="article">
                  <span className="section-label">{newsData.page4.rashtriya.tag}</span>
                  <h2 className="hl hl-md">{newsData.page4.rashtriya.headline}</h2>
                  <div className="body">
                    {newsData.page4.rashtriya.body.map((p, i) => <p key={i}>{p}</p>)}
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">बांदा</span>
                  <h2 className="hl hl-sm">समूह अवधारणा और क्षमता वृद्धि का हुआ समापन — पांच दिवसीय प्रशिक्षण का किया गया था आयोजन</h2>
                  <div className="body">
                    <p>राष्ट्रीय शहरी आजीविका मिशन के अंतर्गत गठित स्व सहायता समूहों का 5 दिवसीय समूह अवधारणा एवं क्षमता वृद्धि प्रशिक्षण कार्यक्रम का समापन हुआ।</p>
                    <p>15 स्व सहायता समूहों की करीब 120 महिलाओं को समूह के संचालन, नियमित बैठक, नियमित बचत, आंतरिक ऋण, बही खाता रखरखाव एवं ऋण चुकौती के बारे विस्तार से समझाया गया।</p>
                  </div>
                  <hr className="rule-thin" />
                  <span className="section-label">नगर पालिका परिषद</span>
                  <h2 className="hl hl-sm">गोला गोकर्णनाथ — 'स्वच्छ भारत अभियान' सर्वे 2023-24</h2>
                  <div className="box">
                    <div className="box-body" style={{fontSize:'9px'}}>
                      नगर पालिका परिषद गोला गोकर्णनाथ (खीरी) — स्वच्छ भारत अभियान नगरीय के अंतर्गत निर्धारित प्रश्नों पर जनता की प्रतिक्रिया पोर्टल पर दर्ज करना सुनिश्चित करें।<br/><br/>
                      <strong>Helpline:</strong> https://sbmurban.org/feedback<br/>
                      <strong>अधिशासी अधिकारी:</strong> विजय कुमार शुक्ल 'रिंकू'<br/>
                      <strong>नगर पालिका परिषद, गोला गोकर्णनाथ</strong>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}

          {/* EMAIL BAR */}
          <div className="email-bar">indiajunctionnews@gmail.com</div>

          {/* FOOTER */}
          <div className="footer">
            <div>
              <strong>स्वामी, प्रकाशक, मुद्रक</strong> — पीयूष मणि अवस्थी<br />
              स्पेक्ट्राविजन मीडिया प्रा. लि., 89/211 मकबूलगंज, निकट कसाईबाड़ा चौराहा, लखनऊ
            </div>
            <div className="page-num">{activePage}</div>
            <div style={{textAlign:'right'}}>
              <strong>सम्पादक</strong> — पीयूष मणि अवस्थी<br />
              68/325, गांधीनगर, लालकुंआ, लखनऊ (उ.प्र.)<br />
              (सभी विवादों का न्याय क्षेत्र लखनऊ होगा)
            </div>
          </div>

        </div>
      </div>
    </>
  );
}